(() => {
  'use strict';

  const theme = window.theme || {};
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const desktop = window.matchMedia('(min-width: 1024px)');

  const rafThrottle = (fn) => {
    let queued = false;
    return (...args) => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn(...args);
      });
    };
  };

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  const parseHtml = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ------------------------------------------------------------------ */
  /* Money                                                               */
  /* ------------------------------------------------------------------ */
  function formatMoney(cents, format = theme.moneyFormat || '{{amount}}') {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiters = (number, precision, thousands, decimal) => {
      if (number == null || isNaN(number)) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    const match = format.match(placeholder);
    let value;
    switch (match && match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  /* ------------------------------------------------------------------ */
  /* Drawer                                                              */
  /* ------------------------------------------------------------------ */
  class Drawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      clearTimeout(this.closeTimer);
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('scroll-lock');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      const target = qs('[data-autofocus]', this) || qs('.drawer__panel', this);
      setTimeout(() => target && target.focus({ preventScroll: true }), 120);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.closeTimer = setTimeout(() => {
        this.hidden = true;
        if (!qs('x-drawer.is-open') && qs('x-lightbox')?.hidden !== false) {
          document.documentElement.classList.remove('scroll-lock');
        }
      }, 300);
      if (this.trigger && this.trigger.focus) this.trigger.focus({ preventScroll: true });
    }
  }
  customElements.define('x-drawer', Drawer);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && drawer.open) {
        event.preventDefault();
        qsa('x-drawer.is-open').forEach((other) => other !== drawer && other.close());
        drawer.open(opener);
      }
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('x-drawer');
      if (drawer) drawer.close();
    }
  });

  /* ------------------------------------------------------------------ */
  /* Cart                                                                */
  /* ------------------------------------------------------------------ */
  const Cart = {
    sectionIds() {
      return qsa('[data-cart-section]').map((element) => element.dataset.cartSection);
    },

    setBusy(busy) {
      qsa('.cart-drawer, .cart-page').forEach((element) => element.classList.toggle('is-busy', busy));
    },

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const fresh = qs(`[data-cart-section="${id}"]`, parseHtml(html));
        const current = qs(`[data-cart-section="${id}"]`);
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      });
      this.syncCount();
    },

    syncCount(count) {
      const source = qs('[data-cart-count]');
      const value = count != null ? count : source ? source.dataset.cartCount : null;
      if (value == null) return;
      qsa('[data-bag-count]').forEach((element) => {
        element.textContent = value;
        element.dataset.count = value;
      });
    },

    async request(url, options) {
      const response = await fetch(url, options);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.cartError);
      }
      return data;
    },

    async refreshCount() {
      try {
        const response = await fetch(`${theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const data = await response.json();
        this.syncCount(data.item_count);
      } catch (error) {
        /* the count stays as it was */
      }
    },

    showError(error) {
      qsa('[data-cart-error]').forEach((element) => {
        element.textContent = error.message;
        element.hidden = false;
      });
    },

    /* Add one variant by id (add-on products in the cart) */
    async addVariant(variantId, quantity = 1) {
      this.setBusy(true);
      try {
        const data = await this.request(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity }],
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        this.render(data.sections);
        await this.refreshCount();
      } catch (error) {
        this.showError(error);
      } finally {
        this.setBusy(false);
      }
    },

    /* Replace a line by another variant of the same product */
    async swap(key, variantId, quantity) {
      this.setBusy(true);
      try {
        await this.request(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      } catch (error) {
        this.showError(error);
        this.setBusy(false);
        return;
      }
      await this.addVariant(variantId, quantity);
    },

    async add(formData) {
      const ids = this.sectionIds();
      if (ids.length) {
        formData.append('sections', ids.join(','));
        formData.append('sections_url', window.location.pathname);
      }
      const data = await this.request(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      this.render(data.sections);
      return data;
    },

    async change(key, quantity) {
      this.setBusy(true);
      try {
        const data = await this.request(`${theme.routes.cartChange}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        this.render(data.sections);
        this.syncCount(data.item_count);
      } catch (error) {
        this.showError(error);
      } finally {
        this.setBusy(false);
      }
    },
  };

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-qty-change]');
    if (quantityButton) {
      const line = quantityButton.closest('[data-line-key]');
      const next = Math.max(0, Number(line.dataset.quantity) + Number(quantityButton.dataset.qtyChange));
      Cart.change(line.dataset.lineKey, next);
      return;
    }
    const removeButton = event.target.closest('[data-line-remove]');
    if (removeButton) {
      event.preventDefault();
      Cart.change(removeButton.closest('[data-line-key]').dataset.lineKey, 0);
      return;
    }
    const quickAdd = event.target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault();
      Cart.addVariant(quickAdd.dataset.quickAdd);
      return;
    }
    const quickOpen = event.target.closest('[data-quick-open]');
    if (quickOpen) {
      const panel = quickOpen.closest('.cart-panel');
      const sheet = panel ? qs('[data-cart-quick]', panel) : null;
      const source = qs('[data-quick-options]', quickOpen.closest('.cart-rec'));
      if (!sheet || !source) return;
      qs('[data-quick-target]', sheet).innerHTML = source.innerHTML;
      sheet.hidden = false;
      return;
    }
    if (event.target.closest('[data-quick-close]')) {
      event.target.closest('[data-cart-quick]').hidden = true;
      return;
    }
    const quickConfirm = event.target.closest('[data-quick-confirm]');
    if (quickConfirm) {
      const select = qs('[data-quick-select]', quickConfirm.closest('[data-cart-quick]'));
      if (select && select.value) Cart.addVariant(select.value);
      return;
    }
    const totalToggle = event.target.closest('[data-total-toggle]');
    if (totalToggle) {
      const details = qs('[data-total-details]', totalToggle.closest('.cart-total'));
      details.hidden = !details.hidden;
      totalToggle.textContent = details.hidden ? totalToggle.dataset.show : totalToggle.dataset.hide;
      totalToggle.setAttribute('aria-expanded', details.hidden ? 'false' : 'true');
      return;
    }
    const recsStep = event.target.closest('[data-recs-step]');
    if (recsStep) {
      const list = qs('[data-recs-list]', recsStep.closest('[data-cart-recs]'));
      const card = list.firstElementChild;
      if (card) list.scrollBy({ left: Number(recsStep.dataset.recsStep) * (card.offsetWidth + 8), behavior: 'smooth' });
      return;
    }
    const summaryToggle = event.target.closest('[data-cart-summary-toggle]');
    if (summaryToggle) {
      const page = summaryToggle.closest('[data-cart-page]');
      const bar = qs('[data-cart-bar]', page);
      if (bar) page.style.setProperty('--cart-bar-height', `${bar.offsetHeight}px`);
      const open = page.classList.toggle('is-summary-open');
      summaryToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  });

  document.addEventListener('change', (event) => {
    const quantity = event.target.closest('[data-line-quantity]');
    if (quantity) {
      Cart.change(quantity.closest('[data-line-key]').dataset.lineKey, Number(quantity.value));
      return;
    }
    const select = event.target.closest('[data-line-variant]');
    if (!select) return;
    const line = select.closest('[data-line-key]');
    Cart.swap(line.dataset.lineKey, select.value, Number(line.dataset.quantity));
  });

  /* Arrows above the suggestions follow the scroll position */
  document.addEventListener(
    'scroll',
    (event) => {
      const list = event.target;
      if (!list.matches || !list.matches('[data-recs-list]')) return;
      const arrows = qsa('[data-recs-step]', list.closest('[data-cart-recs]'));
      const end = list.scrollWidth - list.clientWidth - 2;
      arrows.forEach((arrow) => {
        arrow.disabled = arrow.dataset.recsStep === '-1' ? list.scrollLeft <= 2 : list.scrollLeft >= end;
      });
    },
    true
  );

  class CartPerks extends HTMLElement {
    connectedCallback() {
      const list = qs('[data-perks-list]', this);
      const count = list ? list.children.length : 0;
      if (count < 2) return;
      let index = 0;
      this.timer = setInterval(() => {
        index = (index + 1) % count;
        list.style.transform = `translateY(-${index * this.offsetHeight}px)`;
      }, 3000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('cart-perks', CartPerks);

  document.addEventListener(
    'change',
    debounce((event) => {
      if (!event.target.matches('[data-cart-note]')) return;
      fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: event.target.value }),
      });
    }, 300)
  );

  /* ------------------------------------------------------------------ */
  /* Product page                                                        */
  /* ------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const data = qs('[data-variants]', this);
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.variantsUrl = data ? data.dataset.more : null;
      this.form = qs('[data-product-form]', this);
      this.idInput = this.form ? qs('[data-variant-id]', this.form) : null;
      this.gallery = qs('product-gallery', this);
      this.groups = qsa('[data-option-group]', this);
      this.colorGroup = qs('.product-option--color', this);
      this.sizeGroup = qs('.product-option--size', this);
      const chartSource = qs('[data-size-chart]', this);
      try {
        this.sizeChart = chartSource ? JSON.parse(chartSource.textContent) : null;
      } catch (error) {
        this.sizeChart = null;
      }

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) this.onOptionChange();
      });
      this.addEventListener('click', (event) => this.onClick(event));
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

      const preset = this.applyUrlOptions();
      this.currentVariant = this.findVariant();
      this.updateAvailability();
      this.updateMeasure();
      this.initSticky();
      if (this.variantsUrl) this.loadVariants(2);

      requestAnimationFrame(() => {
        if (this.gallery && this.gallery.setup) {
          this.gallery.setup(this.variants);
          const first = this.currentVariant || this.matchPartial(this.selected());
          if (this.gallery.mode === 'group' && first && first.featured_media) {
            this.gallery.setActive(first.featured_media.id);
          }
        }
        if (preset) this.onOptionChange();
      });
    }

    /* A link can name option values (?opt_color=Navy&opt_size=M); swatches that lead to another product use it */
    optionParam(group) {
      return `opt_${(group.dataset.optionName || '').trim().toLowerCase()}`;
    }

    applyUrlOptions() {
      const params = new URLSearchParams(window.location.search);
      let changed = false;
      this.groups.forEach((group) => {
        const wanted = params.get(this.optionParam(group));
        if (!wanted) return;
        const input = qsa('[data-option-input]', group).find((candidate) => candidate.value === wanted);
        if (!input || input.checked) return;
        input.checked = true;
        changed = true;
      });
      return changed;
    }

    /* Liquid lists 250 variants at most, the rest comes in per page of 250 */
    loadVariants(page) {
      const join = this.variantsUrl.includes('?') ? '&' : '?';
      fetch(`${this.variantsUrl}${join}section_id=product-variants&page=${page}`)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const source = qs('[data-variants-page]', new DOMParser().parseFromString(html, 'text/html'));
          const batch = source ? JSON.parse(source.textContent) : [];
          const known = new Set(this.variants.map((variant) => variant.id));
          const fresh = batch.filter((variant) => !known.has(variant.id));
          if (!fresh.length) return;
          this.variants = this.variants.concat(fresh);
          if (this.gallery && this.gallery.setup) this.gallery.setup(this.variants);
          this.updateAvailability();
          if (this.groups.length && !this.selected().includes(null) && !this.currentVariant) this.onOptionChange();
          if (batch.length >= 250) this.loadVariants(page + 1);
        })
        .catch(() => {
          /* the variants that are already listed keep working */
        });
    }

    selected() {
      return this.groups.map((group) => {
        const input = qs('[data-option-input]:checked', group);
        return input ? input.value : null;
      });
    }

    /* Full name of an option value, when the group lists one (S becomes Small) */
    valueName(group, value) {
      const names = (group.dataset.valueNames || '').split('||');
      const match = names.map((pair) => pair.split('::')).find((pair) => pair[0] === value);
      return match && match[1] ? match[1] : value;
    }

    findVariant(options = this.selected()) {
      if (!this.groups.length) {
        const id = this.idInput ? Number(this.idInput.value) : null;
        return this.variants.find((variant) => variant.id === id) || this.variants[0] || null;
      }
      if (options.includes(null)) return null;
      return this.variants.find((variant) => variant.options.every((value, index) => value === options[index])) || null;
    }

    matchPartial(options) {
      const fits = (variant) => options.every((value, index) => value === null || variant.options[index] === value);
      return this.variants.find((variant) => variant.available && fits(variant)) || this.variants.find(fits) || null;
    }

    onOptionChange() {
      const options = this.selected();
      this.groups.forEach((group, index) => {
        const label = qs('[data-option-value]', group);
        if (label && options[index] !== null) label.textContent = this.valueName(group, options[index]);
        if (options[index] !== null) this.clearMissing(group);
      });

      this.updateAvailability();
      this.updateStickyColors(options);
      this.updateMeasure();

      const complete = !options.includes(null);
      const variant = this.findVariant(options);
      const reference = variant || this.matchPartial(options);
      this.currentVariant = variant;

      if (reference && reference.featured_media && this.gallery && this.gallery.setActive) {
        this.gallery.setActive(reference.featured_media.id);
      }

      if (!complete) {
        if (reference) this.updatePrice(reference);
        this.updateButtons(null, true);
        return;
      }
      this.updateVariant(variant);
    }

    updateVariant(variant) {
      this.updateButtons(variant, false);
      if (!variant) return;

      if (this.idInput) this.idInput.value = variant.id;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      this.updatePrice(variant);
      qsa('[data-product-sku]').forEach((element) => {
        element.textContent = variant.sku || '';
      });
      const stickyVariant = qs('[data-sticky-variant]', this);
      if (stickyVariant) stickyVariant.textContent = variant.title;
      const stickyImage = qs('[data-sticky-image]', this);
      if (stickyImage && variant.featured_image && variant.featured_image.src) {
        const source = variant.featured_image.src;
        stickyImage.src = source + (source.includes('?') ? '&' : '?') + 'width=120';
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updatePrice(variant) {
      const onSale = variant.compare_at_price > variant.price;
      qsa('[data-price]', this).forEach((element) => {
        element.classList.toggle('price--on-sale', onSale);
        element.innerHTML =
          `<span class="price__current">${formatMoney(variant.price)}</span>` +
          (onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '');
      });
      qsa('[data-price-badge]', this).forEach((badge) => {
        const percent = onSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
        badge.hidden = percent === 0;
        badge.textContent = badge.dataset.template.replace('[percent]', percent);
      });
    }

    updateButtons(variant, incomplete) {
      let label = (this.form && this.form.dataset.addLabel) || theme.strings.addToCart;
      let disabled = false;
      if (!incomplete) {
        if (!variant) {
          label = theme.strings.unavailable;
          disabled = true;
        } else if (!variant.available) {
          label = theme.strings.soldOut;
          disabled = true;
        }
      }
      qsa('[data-add-to-cart], [data-sticky-add]', this).forEach((button) => {
        button.disabled = disabled;
        const text = qs('[data-add-to-cart-text]', button);
        if (text) text.textContent = label;
      });
    }

    updateAvailability() {
      if (!this.groups.length) return;
      const options = this.selected();
      this.groups.forEach((group, index) => {
        qsa('[data-option-input]', group).forEach((input) => {
          const test = options.slice();
          test[index] = input.value;
          const available = this.variants.some(
            (variant) => variant.available && test.every((value, position) => value === null || variant.options[position] === value)
          );
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }

    /* Measurements of the chosen size, taken from the size chart */
    updateMeasure() {
      const bar = qs('[data-size-measure]', this);
      if (!bar) return;
      const chart = this.sizeChart;
      const input = this.sizeGroup ? qs('[data-option-input]:checked', this.sizeGroup) : null;
      if (!chart || !input || !Array.isArray(chart.rows) || !Array.isArray(chart.columns)) {
        bar.hidden = true;
        return;
      }
      const normalize = (value) => String(value == null ? '' : value).trim().toLowerCase();
      const selected = normalize(input.value);
      const row =
        chart.rows.find((candidate) => normalize(candidate.size) === selected) ||
        chart.rows.find((candidate) => {
          const size = normalize(candidate.size);
          return size && (selected.startsWith(`${size} `) || selected.startsWith(`${size}(`));
        });
      if (!row) {
        bar.hidden = true;
        return;
      }
      const unit = chart.unit === 'in' ? 'IN' : 'CM';
      const items = chart.columns
        .filter((column) => row[column.key] != null && row[column.key] !== '')
        .map((column) => {
          const suffix = !column.type || column.type === 'length' ? ` ${unit}` : '';
          return `<span class="size-measure__item">${escapeHtml(column.label || column.key)}<b>${escapeHtml(row[column.key])}${suffix}</b></span>`;
        });
      if (!items.length) {
        bar.hidden = true;
        return;
      }
      qs('[data-size-measure-list]', bar).innerHTML = items.join('');
      bar.hidden = false;
    }

    clearMissing(group) {
      group.classList.remove('is-missing');
      const error = qs('[data-option-error]', group);
      if (error) error.hidden = true;
    }

    promptSelection(group) {
      group.classList.remove('is-missing');
      void group.offsetWidth;
      group.classList.add('is-missing');
      const error = qs('[data-option-error]', group);
      if (error) error.hidden = false;
      const rect = group.getBoundingClientRect();
      if (rect.top < 80 || rect.bottom > window.innerHeight - 100) {
        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      const missing = this.groups.find((group) => !qs('[data-option-input]:checked', group));
      if (missing) {
        this.promptSelection(missing);
        return;
      }
      if (!this.currentVariant || !this.currentVariant.available) return;

      const buttons = qsa('[data-add-to-cart], [data-sticky-add]', this);
      if (buttons.some((button) => button.classList.contains('is-loading'))) return;
      const error = qs('[data-form-error]', this);
      if (error) error.hidden = true;
      buttons.forEach((button) => button.classList.add('is-loading'));

      try {
        await Cart.add(new FormData(this.form));
        const drawer = document.getElementById('CartDrawer');
        if (theme.cartType === 'drawer' && drawer && drawer.open) {
          drawer.open();
        } else if (theme.cartType === 'page') {
          window.location.href = theme.routes.cart;
        } else if (theme.cartType === 'app') {
          /* The cart app notices the new line and opens by itself */
          Cart.refreshCount();
        } else {
          qsa('[data-check-cart]', this).forEach((link) => {
            link.hidden = false;
          });
          Cart.refreshCount();
        }
      } catch (failure) {
        if (error) {
          error.textContent = failure.message;
          error.hidden = false;
        }
      } finally {
        buttons.forEach((button) => button.classList.remove('is-loading'));
      }
    }

    onClick(event) {
      const optionLink = event.target.closest('[data-option-link]');
      if (optionLink) {
        /* Take the choices made here along to the other product */
        const url = new URL(optionLink.href);
        this.groups.forEach((group) => {
          const input = qs('[data-option-input]:checked', group);
          const key = this.optionParam(group);
          if (input && group !== this.colorGroup && !url.searchParams.has(key)) url.searchParams.set(key, input.value);
        });
        optionLink.href = url.toString();
        return;
      }
      const swatch = event.target.closest('[data-sticky-swatch]');
      if (swatch && this.colorGroup) {
        const input = qsa('[data-option-input]', this.colorGroup).find((candidate) => candidate.value === swatch.dataset.stickySwatch);
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      if (event.target.closest('[data-sticky-more]') && this.colorGroup) {
        this.colorGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (event.target.closest('[data-scroll-to-reviews]')) {
        const target = qs(
          '[data-reviews-anchor], #judgeme_product_reviews, .jdgm-review-widget, #shopify-product-reviews, #looxReviews, .yotpo-main-widget, .stamped-main-widget'
        );
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    updateStickyColors(options) {
      const bar = qs('[data-sticky-colors]', this);
      if (!bar || !this.colorGroup) return;
      const value = options[this.groups.indexOf(this.colorGroup)];
      if (value == null) return;
      const label = qs('[data-sticky-color-value]', bar);
      if (label) label.textContent = value;
      qsa('[data-sticky-swatch]', bar).forEach((swatch) => swatch.classList.toggle('is-active', swatch.dataset.stickySwatch === value));
    }

    initSticky() {
      const bar = qs('[data-sticky-atc]', this);
      const colors = qs('[data-sticky-colors]', this);
      const button = qs('[data-add-to-cart]', this);
      if (!button || (!bar && !colors)) return;
      [bar, colors].forEach((element) => {
        if (element) element.hidden = false;
      });

      const update = () => {
        const past = button.getBoundingClientRect().bottom < 60;
        if (bar) bar.classList.toggle('is-visible', past);
        if (colors) {
          const reference = (this.colorGroup || button).getBoundingClientRect();
          const below = reference.top > window.innerHeight - 30;
          colors.classList.toggle('is-visible', below && !past && !desktop.matches);
        }
      };
      const onScroll = rafThrottle(update);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    }
  }
  customElements.define('product-info', ProductInfo);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.list = qs('[data-gallery-list]', this);
      this.thumbs = qs('[data-gallery-thumbs]', this);
      this.mode = this.dataset.mode || 'all';
      this.groups = null;
      if (!this.list) return;

      this.list.addEventListener('scroll', rafThrottle(() => this.syncThumb()), { passive: true });

      if (this.thumbs) {
        this.thumbs.addEventListener('click', (event) => {
          const thumb = event.target.closest('[data-thumb]');
          if (!thumb) return;
          const item = this.item(thumb.dataset.mediaId);
          if (item) this.list.scrollTo({ left: item.offsetLeft - this.list.offsetLeft, behavior: 'smooth' });
        });
      }

      if (this.hasAttribute('data-zoom')) {
        this.list.addEventListener('click', (event) => {
          const image = event.target.closest('.gallery__image');
          const lightbox = qs('x-lightbox');
          if (!image || !lightbox || !lightbox.open) return;
          const images = qsa('.gallery__item:not([hidden]) .gallery__image', this.list);
          lightbox.open(
            images.map((element) => element.dataset.zoomSrc || element.currentSrc || element.src),
            Math.max(0, images.indexOf(image))
          );
        });
      }
    }

    item(mediaId) {
      return qs(`.gallery__item[data-media-id="${mediaId}"]`, this.list);
    }

    setup(variants) {
      if (this.mode !== 'group' || !this.list) return;
      const featured = new Set(
        variants.map((variant) => (variant.featured_media ? String(variant.featured_media.id) : null)).filter(Boolean)
      );
      if (featured.size < 2) return;
      const items = qsa('.gallery__item[data-media-id]', this.list).sort(
        (first, second) => Number(first.dataset.position) - Number(second.dataset.position)
      );
      let current = null;
      this.groups = new Map();
      items.forEach((item) => {
        const id = item.dataset.mediaId;
        if (featured.has(id)) current = id;
        this.groups.set(id, current);
      });
    }

    setActive(mediaId) {
      if (!this.list) return;
      const id = String(mediaId);
      const item = this.item(id);
      if (!item) return;

      if (this.groups) {
        const group = this.groups.get(id);
        qsa('[data-media-id]', this).forEach((element) => {
          const own = this.groups.get(element.dataset.mediaId);
          element.hidden = !(own === null || own === undefined || own === group);
        });
      }

      if (this.list.firstElementChild !== item) this.list.prepend(item);
      if (this.thumbs) {
        const thumb = qs(`[data-thumb][data-media-id="${id}"]`, this.thumbs);
        if (thumb && this.thumbs.firstElementChild !== thumb) this.thumbs.prepend(thumb);
        this.thumbs.scrollTo({ left: 0 });
      }
      this.list.scrollTo({ left: 0 });
      this.syncThumb();
    }

    syncThumb() {
      if (!this.thumbs) return;
      const items = qsa('.gallery__item:not([hidden])', this.list);
      const width = this.list.clientWidth || 1;
      const index = Math.min(items.length - 1, Math.max(0, Math.round(this.list.scrollLeft / width)));
      const activeId = items[index] ? items[index].dataset.mediaId : null;
      qsa('[data-thumb]', this.thumbs).forEach((thumb) => {
        const active = thumb.dataset.mediaId === activeId;
        if (active && !thumb.classList.contains('is-active')) {
          this.thumbs.scrollTo({ left: thumb.offsetLeft - this.thumbs.offsetLeft - 60, behavior: 'smooth' });
        }
        thumb.classList.toggle('is-active', active);
      });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class Lightbox extends HTMLElement {
    connectedCallback() {
      this.stage = qs('[data-lightbox-stage]', this);
      this.sources = [];
      this.index = 0;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-lightbox-prev]')) this.go(-1);
        else if (event.target.closest('[data-lightbox-next]')) this.go(1);
        else if (event.target.closest('[data-lightbox-close]') || event.target.tagName === 'IMG') this.close();
      });
      document.addEventListener('keydown', (event) => {
        if (this.hidden) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'ArrowLeft') this.go(-1);
        if (event.key === 'ArrowRight') this.go(1);
      });
    }

    open(sources, index = 0) {
      if (!sources || !sources.length || !this.stage) return;
      this.sources = sources;
      this.index = index;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('scroll-lock');
      qsa('.lightbox__nav', this).forEach((button) => {
        button.hidden = sources.length < 2;
      });
      this.render();
    }

    render() {
      const image = new Image();
      image.alt = '';
      image.src = this.sources[this.index];
      this.stage.replaceChildren(image);
      this.stage.scrollTop = 0;
    }

    go(direction) {
      this.index = (this.index + direction + this.sources.length) % this.sources.length;
      this.render();
    }

    close() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      if (!qs('x-drawer.is-open')) document.documentElement.classList.remove('scroll-lock');
    }
  }
  customElements.define('x-lightbox', Lightbox);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      if (this.hasAttribute('data-eager')) {
        this.load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(this);
    }

    get tab() {
      return this.dataset.tabId ? document.getElementById(this.dataset.tabId) : null;
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;
      const target = qs('[data-recs-target]', this);
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) throw new Error('Request failed');
        const source = qs('[data-recs-content]', parseHtml(await response.text()));
        if (!source || !source.children.length || !target) throw new Error('Empty');
        target.innerHTML = source.innerHTML;
        if (this.dataset.recGroup) this.hidden = false;
        this.dispatchEvent(new CustomEvent('recs:change', { bubbles: true }));
        if (this.tab) {
          this.tab.hidden = false;
          const tabs = this.tab.closest('x-tabs');
          if (tabs && tabs.ensureActive) tabs.ensureActive();
        }
        const carousel = qs('x-carousel', this);
        if (carousel && carousel.update) carousel.update();
      } catch (error) {
        if (target) target.innerHTML = '';
        if (this.hasAttribute('data-hide-when-empty') || this.dataset.recGroup) this.hidden = true;
        this.dispatchEvent(new CustomEvent('recs:change', { bubbles: true }));
        if (this.tab) {
          this.tab.hidden = true;
          const tabs = this.tab.closest('x-tabs');
          if (tabs && tabs.ensureActive) tabs.ensureActive();
        }
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------ */
  /* Tabs & carousel                                                     */
  /* ------------------------------------------------------------------ */
  class Tabs extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-tab]');
        if (tab && tab.closest('x-tabs') === this) this.select(tab);
      });
      this.addEventListener('keydown', (event) => {
        if (!event.target.matches('[data-tab]')) return;
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        const visible = this.tabs.filter((tab) => !tab.hidden);
        const index = visible.indexOf(event.target);
        const next = visible[(index + (event.key === 'ArrowRight' ? 1 : -1) + visible.length) % visible.length];
        if (next) {
          this.select(next);
          next.focus();
        }
      });
    }

    get tabs() {
      return qsa('[data-tab]', this).filter((tab) => tab.closest('x-tabs') === this);
    }

    panel(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }

    select(tab) {
      this.tabs.forEach((other) => {
        const active = other === tab;
        other.setAttribute('aria-selected', active ? 'true' : 'false');
        other.tabIndex = active ? 0 : -1;
        const panel = this.panel(other);
        if (panel) panel.hidden = !active;
      });
      const panel = this.panel(tab);
      if (panel) {
        qsa('x-carousel', panel).forEach((carousel) => carousel.update && carousel.update());
      }
    }

    ensureActive() {
      const visible = this.tabs.filter((tab) => !tab.hidden);
      if (!visible.length) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const current = visible.find((tab) => tab.getAttribute('aria-selected') === 'true');
      if (!current) this.select(visible[0]);
    }
  }
  customElements.define('x-tabs', Tabs);

  /* One horizontal strip with several product groups; the titles above jump to a group */
  class RecStrip extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-rec-track]', this);
      this.prev = qs('[data-rec-prev]', this);
      this.next = qs('[data-rec-next]', this);
      if (!this.track) return;

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-rec-tab]');
        if (!tab) return;
        const group = this.group(tab.dataset.recTab);
        if (group) this.track.scrollTo({ left: group.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      });
      this.track.addEventListener('scroll', rafThrottle(() => this.update()), { passive: true });
      if (this.prev) this.prev.addEventListener('click', () => this.move(-1));
      if (this.next) this.next.addEventListener('click', () => this.move(1));
      this.addEventListener('recs:change', () => this.refresh());
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      this.refresh();
    }

    get tabs() {
      return qsa('[data-rec-tab]', this);
    }

    group(name) {
      return qs(`[data-rec-group="${name}"]`, this.track);
    }

    refresh() {
      this.tabs.forEach((tab) => {
        const group = this.group(tab.dataset.recTab);
        tab.hidden = !group || group.hidden;
      });
      this.hidden = !this.tabs.some((tab) => !tab.hidden);
      if (!this.hidden) this.update();
    }

    move(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: 'smooth' });
    }

    update() {
      const left = this.track.scrollLeft;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.hidden = left <= 2;
      if (this.next) this.next.hidden = left >= max - 2;

      const visible = this.tabs.filter((tab) => !tab.hidden);
      let active = visible[0] || null;
      visible.forEach((tab) => {
        const group = this.group(tab.dataset.recTab);
        if (group && group.offsetLeft - this.track.offsetLeft <= left + 40) active = tab;
      });
      if (max > 2 && left >= max - 2) active = visible[visible.length - 1];
      this.tabs.forEach((tab) => {
        const on = tab === active;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
  }
  customElements.define('rec-strip', RecStrip);

  class OffersBox extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa('[data-offers-tab]', this);
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-offers-tab]');
        if (tab) {
          this.select(tab);
          return;
        }
        if (event.target.closest('[data-offers-prev]')) this.step(-1);
        if (event.target.closest('[data-offers-next]')) this.step(1);
      });
      qsa('[data-offers-cards]', this).forEach((list) => {
        list.addEventListener('scroll', rafThrottle(() => this.syncDots(list)), { passive: true });
      });
    }

    select(tab) {
      this.tabs.forEach((other) => {
        const on = other === tab;
        other.classList.toggle('is-active', on);
        other.setAttribute('aria-selected', on ? 'true' : 'false');
        const panel = qs(`[data-offers-panel="${other.dataset.offersTab}"]`, this);
        if (panel) panel.hidden = !on;
      });
      const strip = tab.parentElement;
      strip.scrollTo({ left: tab.offsetLeft - strip.offsetLeft - 40, behavior: 'smooth' });
    }

    step(direction) {
      if (!this.tabs.length) return;
      const index = this.tabs.findIndex((tab) => tab.classList.contains('is-active'));
      this.select(this.tabs[(index + direction + this.tabs.length) % this.tabs.length]);
    }

    syncDots(list) {
      const cards = qsa('.offers__card', list);
      const dots = qsa('.offers__dot', list.parentElement);
      if (!cards.length || !dots.length) return;
      const step = cards[0].offsetWidth + 8;
      const index = Math.min(dots.length - 1, Math.round(list.scrollLeft / step));
      dots.forEach((dot, position) => dot.classList.toggle('is-active', position === index));
    }
  }
  customElements.define('offers-box', OffersBox);

  /* Counts down to one fixed moment and removes itself once that moment has passed */
  class SaleCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Number(this.dataset.end) * 1000;
      if (!this.end) return;
      this.fields = {
        days: qs('[data-countdown-days]', this),
        hours: qs('[data-countdown-hours]', this),
        minutes: qs('[data-countdown-minutes]', this),
        seconds: qs('[data-countdown-seconds]', this),
      };
      this.dayParts = qsa('[data-countdown-days-unit]', this);
      this.clock = qs('[data-countdown-clock]', this);
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      const left = Math.floor((this.end - Date.now()) / 1000);
      if (left <= 0) {
        window.clearInterval(this.timer);
        this.hidden = true;
        return;
      }
      const pad = (value) => String(value).padStart(2, '0');
      const days = Math.floor(left / 86400);
      if (this.clock) {
        // One line of text, in the same units as the boxes on the product page
        const [d, h, m, s] = (this.dataset.units || 'd,h,m,s').split(',');
        const parts = [`${pad(Math.floor((left % 86400) / 3600))}${h}`, `${pad(Math.floor((left % 3600) / 60))}${m}`, `${pad(left % 60)}${s}`];
        if (days > 0) parts.unshift(`${days}${d}`);
        this.clock.textContent = parts.join(' ');
        return;
      }
      this.dayParts.forEach((part) => {
        part.hidden = days === 0;
      });
      if (this.fields.days) this.fields.days.textContent = pad(days);
      if (this.fields.hours) this.fields.hours.textContent = pad(Math.floor((left % 86400) / 3600));
      if (this.fields.minutes) this.fields.minutes.textContent = pad(Math.floor((left % 3600) / 60));
      if (this.fields.seconds) this.fields.seconds.textContent = pad(left % 60);
    }
  }
  customElements.define('sale-countdown', SaleCountdown);

  /* Announcement bar: with more than one message they take turns, the next one slides in from the right */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('[data-announcement-slide]', this);
      if (this.slides.length < 2) return;
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.delay = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.holds = new Set();

      // Reading a message keeps it in place
      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => this.hold('hover', true));
        this.addEventListener('mouseleave', () => this.hold('hover', false));
      }
      this.addEventListener('focusin', () => this.hold('focus', true));
      this.addEventListener('focusout', () => this.hold('focus', false));
      this.onVisibility = () => this.hold('hidden', document.hidden);
      document.addEventListener('visibilitychange', this.onVisibility);

      // Theme editor: show the message that is being edited
      this.addEventListener('shopify:block:select', (event) => {
        this.hold('editor', true);
        this.show(this.slides.indexOf(event.target.closest('[data-announcement-slide]')));
      });
      this.addEventListener('shopify:block:deselect', () => this.hold('editor', false));

      this.start();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.cleanup);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    hold(reason, on) {
      if (on) this.holds.add(reason);
      else this.holds.delete(reason);
      this.start();
    }

    start() {
      clearTimeout(this.timer);
      if (this.holds.size) return;
      this.timer = setTimeout(() => {
        this.show((this.index + 1) % this.slides.length);
        this.start();
      }, this.delay);
    }

    show(next) {
      if (next < 0 || next === this.index) return;
      const from = this.slides[this.index];
      const to = this.slides[next];
      this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
      from.classList.remove('is-active');
      from.classList.add('is-leaving');
      from.setAttribute('aria-hidden', 'true');
      to.classList.add('is-active');
      to.removeAttribute('aria-hidden');
      this.index = next;
      // Back to the right-hand side, out of sight, ready for its next turn
      clearTimeout(this.cleanup);
      this.cleanup = setTimeout(() => from.classList.remove('is-leaving'), 600);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* Testimonials: one card in the middle, its neighbours beside it. With more than three
     cards the row goes round in a circle. */
  class TestimonialSlider extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('[data-slide]', this);
      this.dots = qsa('[data-slider-dot]', this);
      this.count = this.slides.length;
      if (!this.count) return;
      this.index = Math.min(Math.max(parseInt(this.dataset.start, 10) || 0, 0), this.count - 1);
      this.offsets = this.slides.map((slide, i) => i - this.index);
      this.place(0, true);
      if ('ResizeObserver' in window) new ResizeObserver(() => this.fit()).observe(this);

      this.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-slider-dot]');
        if (dot) {
          this.go(Number(dot.dataset.sliderDot));
          return;
        }
        const slide = event.target.closest('[data-slide]');
        if (slide && !this.swiped) this.go(this.slides.indexOf(slide));
      });
      this.addEventListener('shopify:block:select', (event) => {
        this.go(this.slides.indexOf(event.target.closest('[data-slide]')));
      });

      /* Swipe or drag sideways */
      this.addEventListener('pointerdown', (event) => {
        if (event.target.closest('[data-slider-dot]') || (event.pointerType === 'mouse' && event.button !== 0)) return;
        this.startX = event.clientX;
        this.swiped = false;
      });
      this.addEventListener('pointermove', (event) => {
        if (this.startX == null) return;
        const moved = event.clientX - this.startX;
        if (!this.swiped && Math.abs(moved) < 8) return;
        this.swiped = true;
        this.classList.add('is-dragging');
        this.style.setProperty('--drag', `${moved}px`);
      });
      const release = (event) => {
        if (this.startX == null) return;
        const moved = event.clientX - this.startX;
        this.startX = null;
        this.classList.remove('is-dragging');
        this.style.removeProperty('--drag');
        if (this.swiped && Math.abs(moved) > 40) this.go(this.index + (moved < 0 ? 1 : -1));
        /* Let the click that follows a drag pass unnoticed */
        setTimeout(() => {
          this.swiped = false;
        }, 0);
      };
      this.addEventListener('pointerup', release);
      this.addEventListener('pointercancel', release);
      this.addEventListener('pointerleave', release);
    }

    get wraps() {
      return this.count > 3;
    }

    go(target) {
      if (target == null || Number.isNaN(target)) return;
      const count = this.count;
      let next = target;
      if (this.wraps) next = ((target % count) + count) % count;
      else next = Math.min(Math.max(target, 0), count - 1);
      if (next === this.index) return;
      let step = next - this.index;
      if (this.wraps) {
        if (step > count / 2) step -= count;
        else if (step < -count / 2) step += count;
      }
      this.index = next;
      this.place(step, false);
    }

    place(step, instant) {
      const count = this.count;
      const half = Math.floor(count / 2);
      this.slides.forEach((slide, i) => {
        let offset = i - this.index;
        if (this.wraps) offset = ((((offset + half) % count) + count) % count) - half;
        /* A card that moves to the other end of the row does so out of sight, without sliding across */
        const jumps = instant || offset !== this.offsets[i] - step;
        slide.classList.toggle('is-jumping', jumps);
        slide.style.setProperty('--offset', offset);
        slide.classList.toggle('is-selected', i === this.index);
        this.offsets[i] = offset;
      });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.fit();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.slides.forEach((slide) => slide.classList.remove('is-jumping')));
      });
    }

    /* As tall as the card in the middle */
    fit() {
      const track = qs('[data-slider-track]', this);
      const height = this.slides[this.index].offsetHeight;
      if (track && height) track.style.height = `${height}px`;
    }
  }
  customElements.define('testimonial-slider', TestimonialSlider);

  /* Recently viewed: remembers the products seen in this browser and shows the others here */
  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const key = 'recently-viewed';
      const current = this.dataset.handle;
      let handles = [];
      try {
        handles = JSON.parse(window.localStorage.getItem(key) || '[]');
        if (!Array.isArray(handles)) handles = [];
      } catch (error) {
        return;
      }
      const others = handles.filter((handle) => handle && handle !== current);
      if (current) {
        try {
          window.localStorage.setItem(key, JSON.stringify([current, ...others].slice(0, 12)));
        } catch (error) {
          /* storage can be full or blocked */
        }
      }
      const limit = parseInt(this.dataset.limit, 10) || 5;
      const target = qs('[data-recent-target]', this);
      if (!target || !others.length) return;

      const load = async () => {
        const cards = await Promise.all(
          others.slice(0, limit).map(async (handle) => {
            try {
              const response = await fetch(`${this.dataset.root}products/${encodeURIComponent(handle)}?section_id=${this.dataset.sectionId}`);
              if (!response.ok) return null;
              const template = qs('template[data-recent-card]', parseHtml(await response.text()));
              return template ? template.innerHTML.trim() : null;
            } catch (error) {
              return null;
            }
          })
        );
        const found = cards.filter(Boolean);
        if (!found.length) return;
        target.innerHTML = found.join('');
        this.hidden = false;
      };

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 800px 0px' }
      );
      /* A hidden element is never in view, so the footer's neighbour does the watching */
      observer.observe(this.parentElement || document.body);
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* Linked products (length, hem and the like): swaps the page content in place
     and keeps the options the shopper already chose. */
  let productSwapped = false;

  const swapProduct = async (link) => {
    const info = link.closest('product-info');
    const kept = info
      ? qsa('[data-option-group]', info).map((group) => {
          const input = qs('[data-option-input]:checked', group);
          return { name: group.dataset.optionName, value: input ? input.value : null };
        })
      : [];
    link.classList.add('is-loading');
    try {
      const response = await fetch(link.href);
      if (!response.ok) throw new Error('Request failed');
      const next = parseHtml(await response.text());
      const content = qs('#MainContent', next);
      const main = qs('#MainContent');
      if (!content || !main || !qs('product-info', content)) throw new Error('No product');

      const scroll = window.scrollY;
      qsa('x-drawer.is-open', main).forEach((drawer) => drawer.close && drawer.close());
      main.innerHTML = content.innerHTML;
      document.title = next.title;
      window.history.pushState({ productSwap: true }, '', link.href);
      productSwapped = true;
      window.scrollTo(0, scroll);

      const fresh = qs('product-info', main);
      kept.forEach(({ name, value }) => {
        if (value === null) return;
        const group = qsa('[data-option-group]', fresh).find((candidate) => candidate.dataset.optionName === name);
        const input = group ? qsa('[data-option-input]', group).find((candidate) => candidate.value === value) : null;
        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } catch (error) {
      window.location.href = link.href;
    }
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-style-link]');
    if (!link || event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (link.classList.contains('is-active') || link.classList.contains('is-loading')) return;
    swapProduct(link);
  });

  window.addEventListener('popstate', () => {
    if (productSwapped) window.location.reload();
  });

  class Carousel extends HTMLElement {
    connectedCallback() {
      this.track = qs('[data-carousel-track]', this);
      this.prev = qs('[data-carousel-prev]', this);
      this.next = qs('[data-carousel-next]', this);
      if (!this.track) return;
      this.track.addEventListener('scroll', rafThrottle(() => this.update()), { passive: true });
      if (this.prev) this.prev.addEventListener('click', () => this.move(-1));
      if (this.next) this.next.addEventListener('click', () => this.move(1));
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }

    move(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: 'smooth' });
    }

    update() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.prev) this.prev.hidden = this.track.scrollLeft <= 2;
      if (this.next) this.next.hidden = this.track.scrollLeft >= max - 2;
    }
  }
  customElements.define('x-carousel', Carousel);

  /* ------------------------------------------------------------------ */
  /* Size chart                                                          */
  /* ------------------------------------------------------------------ */
  class SizeChart extends HTMLElement {
    connectedCallback() {
      const source = qs('[data-size-chart]', this);
      this.target = qs('[data-size-chart-target]', this);
      if (!source || !this.target) return;
      try {
        this.data = JSON.parse(source.textContent);
      } catch (error) {
        return;
      }
      if (!this.data || !Array.isArray(this.data.rows)) return;
      this.sourceUnit = this.data.unit === 'in' ? 'in' : 'cm';
      this.unit = this.dataset.defaultUnit === 'in' ? 'in' : 'cm';
      this.modal = this.closest('x-drawer');
      (this.modal || this).addEventListener('click', (event) => {
        const button = event.target.closest('[data-unit]');
        if (!button) return;
        this.unit = button.dataset.unit;
        this.render();
      });
      this.render();
    }

    convert(value, column) {
      if (value == null || value === '') return '';
      if (column.type && column.type !== 'length') return value;
      if (this.unit === this.sourceUnit) return value;
      const factor = this.sourceUnit === 'cm' ? 1 / 2.54 : 2.54;
      const step = this.unit === 'in' ? 0.5 : 1;
      return String(value).replace(/\d+(?:[.,]\d+)?/g, (match) => {
        const converted = Math.round((parseFloat(match.replace(',', '.')) * factor) / step) * step;
        return Number.isInteger(converted) ? String(converted) : converted.toFixed(1);
      });
    }

    render() {
      const { rows, fit, note } = this.data;
      const columns = Array.isArray(this.data.columns) ? this.data.columns : [];
      const extras = ['uk', 'eu', 'us'].filter((key) => rows.some((row) => row[key] != null && row[key] !== '') && !columns.some((column) => column.key === key));
      const unitLabel = this.unit === 'in' ? this.dataset.labelIn : this.dataset.labelCm;

      const head =
        `<th>${escapeHtml(this.dataset.labelSize)}</th>` +
        extras.map((key) => `<th>${key.toUpperCase()}</th>`).join('') +
        columns
          .map((column) => {
            const convertible = !column.type || column.type === 'length';
            return `<th>${escapeHtml(column.label || column.key)}${convertible ? ` (${escapeHtml(unitLabel)})` : ''}</th>`;
          })
          .join('');

      const body = rows
        .map(
          (row) =>
            `<tr><td>${escapeHtml(row.size)}</td>` +
            extras.map((key) => `<td>${escapeHtml(row[key])}</td>`).join('') +
            columns.map((column) => `<td>${escapeHtml(this.convert(row[column.key], column))}</td>`).join('') +
            '</tr>'
        )
        .join('');

      const units = ['cm', 'in']
        .map(
          (unit) =>
            `<button type="button" class="size-chart__unit" data-unit="${unit}" aria-pressed="${unit === this.unit}">${escapeHtml(
              unit === 'in' ? this.dataset.labelIn : this.dataset.labelCm
            )}</button>`
        )
        .join('');

      const notes = []
        .concat(Array.isArray(fit) ? fit : fit ? [fit] : [])
        .concat(note ? [note] : [])
        .map((line) => `<li>${escapeHtml(line)}</li>`)
        .join('');

      const how = columns
        .filter((column) => column.how)
        .map((column) => `<div><dt>${escapeHtml(column.label || column.key)}</dt><dd>${escapeHtml(column.how)}</dd></div>`)
        .join('');

      const hasLength = columns.some((column) => !column.type || column.type === 'length');
      const unitsHtml = hasLength ? `<div class="size-chart__units">${units}</div>` : '';
      const slot = this.modal ? qs('[data-size-units]', this.modal) : null;
      if (slot) slot.innerHTML = unitsHtml;
      const title = this.data.title || this.dataset.labelTitle || '';

      if (this.dataset.style === 'tiles') {
        this.target.innerHTML = this.tiles(rows, extras, columns, notes) + (how ? `<dl class="size-chart__how">${how}</dl>` : '');
        return;
      }

      this.target.innerHTML =
        (slot ? '' : unitsHtml) +
        '<div class="size-chart__block">' +
        (title ? `<h3 class="size-chart__title">${escapeHtml(title)}</h3>` : '') +
        `<div class="size-chart__scroll"><table class="size-chart__table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>` +
        (notes ? `<ul class="size-chart__notes">${notes}</ul>` : '') +
        '</div>' +
        (how ? `<dl class="size-chart__how">${how}</dl>` : '');
    }

    /* One tile per size, running from a light to a dark shade of one colour */
    tiles(rows, extras, columns, notes) {
      const shades = [['#fff', 12], ['#fff', 31], ['#fff', 58], ['#fff', 83], ['', 100], ['#000', 72], ['#000', 45]];
      const all = extras.map((key) => ({ key, label: key.toUpperCase(), type: 'text' })).concat(columns);
      const unit = this.unit === 'in' ? 'in' : 'cm';
      const last = Math.max(1, rows.length - 1);
      let widest = 0;

      const cards = rows
        .map((row, index) => {
          const step = Math.round((index / last) * (shades.length - 1));
          const [mix, share] = shades[step];
          const tone = mix ? `color-mix(in srgb, var(--chart-color) ${share}%, ${mix})` : 'var(--chart-color)';
          const measures = all
            .filter((column) => row[column.key] != null && row[column.key] !== '')
            .map((column) => {
              const length = !column.type || column.type === 'length';
              return (
                `<div class="size-tile__measure"><span class="size-tile__label">${escapeHtml(column.label || column.key)}</span>` +
                `<span class="size-tile__value">${escapeHtml(this.convert(row[column.key], column))}` +
                (length ? `<span class="size-tile__unit">${unit}</span>` : '') +
                '</span></div>'
              );
            });
          widest = Math.max(widest, measures.length);
          return (
            `<div class="size-tile${step >= 3 ? ' size-tile--dark' : ''}" style="--tile: ${tone};">` +
            `<div class="size-tile__size">${escapeHtml(row.size)}</div>` +
            `<div class="size-tile__measures">${measures.join('')}</div></div>`
          );
        })
        .join('');

      const noteSource = qs('[data-size-note]', this);
      const noteCard = noteSource
        ? '<div class="size-tile size-tile--note">' +
          (this.dataset.noteLabel ? `<div class="size-tile__note-label">${escapeHtml(this.dataset.noteLabel)}</div>` : '') +
          `<div class="size-tile__note">${noteSource.innerHTML}</div></div>`
        : '';
      const meta = this.dataset.meta ? `<div class="size-tiles__meta">${escapeHtml(this.dataset.meta)}</div>` : '';

      return (
        `<div class="size-tiles${widest > 1 ? ' size-tiles--rows' : ''}">${cards}${noteCard}</div>` +
        meta +
        (notes ? `<ul class="size-chart__notes">${notes}</ul>` : '')
      );
    }
  }
  customElements.define('size-chart', SizeChart);


  /* ------------------------------------------------------------------ */
  /* Header                                                              */
  /* ------------------------------------------------------------------ */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.section-header') || this;
      this.navRow = qs('[data-nav-row]', this);
      this.nav = qs('[data-nav]', this);

      const onScroll = rafThrottle(() => {
        this.wrapper.classList.toggle('is-stuck', desktop.matches && this.wrapper.getBoundingClientRect().top <= -60);
      });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();

      if (this.nav) {
        qsa('.nav__link', this.nav).forEach((link) => {
          link.addEventListener('mouseenter', () => this.openMega(link));
          link.addEventListener('focus', () => this.openMega(link));
        });
      }
      if (this.navRow) {
        this.navRow.addEventListener('mouseleave', () => {
          this.closeTimer = setTimeout(() => this.closeMega(), 140);
        });
        this.navRow.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
      }
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeMega();
      });
      this.addEventListener('focusout', (event) => {
        if (this.navRow && !this.navRow.contains(event.relatedTarget)) this.closeMega();
      });

      this.initNavArrows();

      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-history-back]')) return;
        if (window.history.length > 1) window.history.back();
        else window.location.href = '/';
      });
    }

    openMega(link) {
      clearTimeout(this.closeTimer);
      this.closeMega();
      const id = link.dataset.megaTrigger;
      const panel = id ? document.getElementById(id) : null;
      if (!panel) return;
      panel.classList.add('is-open');
      link.classList.add('is-open');
      link.setAttribute('aria-expanded', 'true');
      this.classList.add('has-open-mega');
    }

    closeMega() {
      qsa('.mega.is-open', this).forEach((panel) => panel.classList.remove('is-open'));
      qsa('.nav__link.is-open', this).forEach((link) => {
        link.classList.remove('is-open');
        link.setAttribute('aria-expanded', 'false');
      });
      this.classList.remove('has-open-mega');
    }

    initNavArrows() {
      const prev = qs('[data-nav-prev]', this);
      const next = qs('[data-nav-next]', this);
      if (!this.nav || !prev || !next) return;
      const update = () => {
        const max = this.nav.scrollWidth - this.nav.clientWidth;
        prev.hidden = this.nav.scrollLeft <= 2;
        next.hidden = this.nav.scrollLeft >= max - 2;
      };
      prev.addEventListener('click', () => this.nav.scrollBy({ left: -240, behavior: 'smooth' }));
      next.addEventListener('click', () => this.nav.scrollBy({ left: 240, behavior: 'smooth' }));
      this.nav.addEventListener('scroll', rafThrottle(update), { passive: true });
      window.addEventListener('resize', rafThrottle(update));
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(this.nav);
      update();
    }
  }
  customElements.define('site-header', SiteHeader);

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.button = qs('[data-localization-button]', this);
      this.panel = qs('[data-localization-panel]', this);
      if (!this.button || !this.panel) return;
      this.button.addEventListener('click', () => this.toggle());
      this.addEventListener('click', (event) => {
        const item = event.target.closest('[data-localization-value]');
        if (!item) return;
        event.preventDefault();
        const form = qs('form', this);
        const input = form ? qs(`input[name="${item.dataset.localizationName}"]`, form) : null;
        if (!input) return;
        input.value = item.dataset.localizationValue;
        form.submit();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.toggle(false);
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.toggle(false);
      });
    }

    toggle(force) {
      const open = typeof force === 'boolean' ? force : this.panel.hidden;
      this.panel.hidden = !open;
      this.button.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }
  customElements.define('localization-form', LocalizationForm);

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', debounce(() => this.search(), 250));
    }

    async search() {
      const term = this.input.value.trim();
      if (this.controller) this.controller.abort();
      if (term.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Request failed');
        const content = qs('[data-predictive-content]', parseHtml(await response.text()));
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------ */
  /* Product cards                                                       */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-card-swatch]');
    if (!swatch) return;
    const card = swatch.closest('[data-product-card]');
    if (!card) return;
    qsa('[data-card-swatch]', card).forEach((other) => other.classList.toggle('is-active', other === swatch));
    const image = qs('[data-card-image]', card);
    if (image && swatch.dataset.image) {
      image.removeAttribute('srcset');
      image.src = swatch.dataset.image;
    }
    if (swatch.dataset.url) {
      qsa('[data-card-link]', card).forEach((link) => {
        link.href = swatch.dataset.url;
      });
    }
  });

  /* ------------------------------------------------------------------ */
  /* Collection filters                                                  */
  /* ------------------------------------------------------------------ */
  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort-select]');
    if (sort) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
      return;
    }
    const form = event.target.closest('[data-filter-form][data-auto-submit]');
    if (form && !event.target.matches('[data-filter-price]')) {
      cleanFilterForm(form);
      form.submit();
    }
  });

  document.addEventListener('submit', (event) => {
    if (event.target.matches('[data-filter-form]')) cleanFilterForm(event.target);
  });

  /* With a cart app the theme does not change the cart itself. Whenever anything does,
     the number at the bag icon is read again. */
  if (theme.cartType === 'app') {
    const touchesCart = (url) => /\/cart\/(add|change|update|clear)/.test(String(url || ''));
    const refresh = debounce(() => Cart.refreshCount(), 400);
    const nativeFetch = window.fetch;
    window.fetch = function (...args) {
      const request = nativeFetch.apply(this, args);
      const url = args[0] && args[0].url ? args[0].url : args[0];
      if (touchesCart(url)) request.then(refresh, () => {});
      return request;
    };
    const nativeOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      if (touchesCart(url)) this.addEventListener('loadend', refresh);
      return nativeOpen.call(this, method, url, ...rest);
    };
  }

  function cleanFilterForm(form) {
    qsa('input[type="number"], input[type="hidden"]', form).forEach((input) => {
      if (input.value === '') input.disabled = true;
    });
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-filters-toggle]');
    if (!toggle) return;
    const layout = qs('[data-collection-layout]');
    if (!layout) return;
    const hidden = layout.classList.toggle('is-filters-hidden');
    applyFilterState(hidden);
    try {
      window.sessionStorage.setItem('filters-hidden', hidden ? '1' : '0');
    } catch (error) {
      /* storage can be blocked */
    }
  });

  function applyFilterState(hidden) {
    const layout = qs('[data-collection-layout]');
    if (!layout) return;
    layout.classList.toggle('is-filters-hidden', hidden);
    const grid = qs('[data-collection-grid]', layout);
    if (grid && grid.dataset.columns) {
      const base = Number(grid.dataset.columns);
      grid.classList.remove(`product-grid--${base}`, `product-grid--${base + 1}`);
      grid.classList.add(`product-grid--${hidden ? base + 1 : base}`);
    }
    qsa('[data-filters-toggle]').forEach((toggle) => {
      const label = qs('[data-filters-toggle-label]', toggle);
      if (label) label.textContent = hidden ? toggle.dataset.showLabel : toggle.dataset.hideLabel;
      toggle.setAttribute('aria-expanded', hidden ? 'false' : 'true');
    });
  }

  /* ------------------------------------------------------------------ */
  /* Small behaviours                                                    */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-collapsible-toggle]');
    if (!toggle) return;
    const body = toggle.previousElementSibling;
    if (!body) return;
    const collapsed = body.classList.toggle('is-collapsed');
    toggle.textContent = collapsed ? toggle.dataset.more : toggle.dataset.less;
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-title-toggle]');
    if (!toggle) return;
    const head = toggle.closest('.product__head');
    if (!head) return;
    const expanded = head.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  });

  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-reviews-more]');
    if (!more) return;
    const section = more.closest('[data-reviews]');
    if (section) {
      qsa('[data-review-extra]', section).forEach((review) => {
        review.hidden = false;
      });
    }
    more.closest('.reviews__more').hidden = true;
  });

  function init() {
    /* Mobile: the shipping and returns rows sit below the detail accordions */
    if (!desktop.matches) {
      const details = qs('[data-product-details]');
      const rows = qsa('.product__info .info-row');
      if (details && rows.length) {
        const wrap = document.createElement('div');
        wrap.className = 'info-rows-mobile';
        rows.forEach((row) => wrap.appendChild(row));
        details.after(wrap);
      }
    }

    qsa('[data-title-toggle]').forEach((toggle) => {
      const title = qs('[data-product-title]', toggle.parentElement);
      if (title && title.scrollHeight > title.clientHeight + 2) toggle.hidden = false;
    });

    if (!desktop.matches) {
      qsa('[data-product-details] details').forEach((details) => {
        details.open = details.hasAttribute('data-open-mobile');
      });
      qsa('[data-footer-details]').forEach((details) => {
        details.open = false;
      });
    }

    try {
      if (window.sessionStorage.getItem('filters-hidden') === '1') applyFilterState(true);
    } catch (error) {
      /* storage can be blocked */
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.theme = Object.assign(theme, { formatMoney, Cart });
})();
